/**
 * Manager PA Layout - re-skinned to match the client-pwa v1 template
 * (light theme, RoniDoc CSS tokens, bottom-nav + header pattern). PA-specific
 * mic + voice modal + query bar + slide-out menu + alerts are preserved.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Mic, MicOff, Calendar, Mail, CheckSquare, Menu, X, LogOut, Sparkles,
  Download, History, Bell, Send, ChevronRight, Settings as SettingsIcon,
  CalendarClock, Users, BarChart3, Wallet, AlertTriangle,
} from 'lucide-react';
import { VoiceWaveform, haptic, SuccessCheck } from '@/components/pwa/animations';
import { useManager, logout as managerLogout } from '@/services/api/manager-auth';
import maClient from '@/services/api/ma-client';
import { cn } from '@/lib/utils';
import { InstallPrompt } from '@/components/pwa/InstallPrompt';
import { Onboarding } from '@/components/pwa/Onboarding';
import { ManagerThemeProvider, useManagerTheme, paAccentVars, ThemeToggle } from './theme';

const NAV_ITEMS = [
  { href: '/pa', icon: Home, label: 'Home', end: true },
  { href: '/pa/voice', icon: Mic, label: 'Ask' },
  { href: '/pa/todo', icon: CheckSquare, label: 'Tasks' },
  { href: '/pa/calendar', icon: Calendar, label: 'Calendar' },
  { href: '/pa/email', icon: Mail, label: 'Email' },
];

function ManagerShell() {
  const { resolvedTheme } = useManagerTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const manager = useManager();
  const [menuOpen, setMenuOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef<any>(null);

  const [showMicModal, setShowMicModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem('pa-onboarded'));

  // Submit query - stores in sessionStorage, navigates to voice page
  const handleQuerySubmit = useCallback((q: string) => {
    if (!q.trim()) return;
    sessionStorage.setItem('pa-pending-query', q.trim());
    if (location.pathname === '/pa/voice' || location.pathname === '/pa/voice/') {
      window.dispatchEvent(new CustomEvent('pa-query', { detail: q.trim() }));
    } else {
      navigate('/pa/voice');
    }
  }, [navigate, location.pathname]);

  // Voice recognition
  const startListening = useCallback(() => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-AU';
    recognition.onstart = () => { setIsListening(true); setTranscript(''); };
    recognition.onresult = (e: any) => {
      let final = '', interim = '';
      for (let i = 0; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      setTranscript(final || interim);
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
  }, []);

  const stopListeningAndSubmit = useCallback(() => {
    if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; }
    setIsListening(false);
    setShowMicModal(false);
    if (transcript.trim()) {
      handleQuerySubmit(transcript.trim());
      setInputText('');
      setTranscript('');
    }
  }, [transcript, handleQuerySubmit]);

  const openMicModal = useCallback(() => {
    setShowMicModal(true);
    setTranscript('');
    setTimeout(() => startListening(), 200);
  }, [startListening]);

  // Brand this app as "Manager Assistant" while mounted: its own PWA manifest,
  // green theme-color, and document title. Restore on unmount so the firm panel
  // keeps its identity.
  useEffect(() => {
    const head = document.head;
    let manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const prevManifest = manifestLink?.href;
    if (!manifestLink) { manifestLink = document.createElement('link'); manifestLink.rel = 'manifest'; head.appendChild(manifestLink); }
    manifestLink.href = '/panel/ma-manifest.json';

    let themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const prevTheme = themeMeta?.content;
    if (!themeMeta) { themeMeta = document.createElement('meta'); themeMeta.name = 'theme-color'; head.appendChild(themeMeta); }
    themeMeta.content = '#35AD88';

    const prevTitle = document.title;
    document.title = 'Manager Assistant';

    return () => {
      if (manifestLink && prevManifest) manifestLink.href = prevManifest;
      if (themeMeta && prevTheme) themeMeta.content = prevTheme;
      document.title = prevTitle;
    };
  }, []);

  // Register service worker and request notification permission
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
      // Auto-apply SW updates so a new deploy refreshes the PA instead of
      // serving the stale cached shell. The SW calls skipWaiting() on install,
      // so a new worker takes control and fires controllerchange -> reload once.
      let reloading = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloading) return;
        reloading = true;
        window.location.reload();
      });
    }
    const timer = setTimeout(() => {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Periodic alert polling for browser notifications
  useEffect(() => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const checkAlerts = async () => {
      try {
        const r = await maClient.get('/manager/alerts');
        {
          const data = r.data;
          const critical = data?.data?.summary?.critical || 0;
          if (critical > 0) {
            const lastNotified = localStorage.getItem('pa-last-notified');
            const now = Date.now();
            if (!lastNotified || now - parseInt(lastNotified) > 60 * 60 * 1000) {
              new Notification('Manager Assistant', {
                body: `${critical} critical alert${critical > 1 ? 's' : ''} need your attention`,
                icon: '/favicon.png',
                tag: 'ronidoc-critical',
              });
              localStorage.setItem('pa-last-notified', String(now));
            }
          }
        }
      } catch {}
    };
    checkAlerts();
    const interval = setInterval(checkAlerts, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    managerLogout();
    window.location.href = '/pa/login';
  };

  const displayName = manager?.displayName || manager?.email?.split('@')[0] || 'Manager';
  const userName = displayName.split(' ')[0];
  const initials = (displayName.split(' ').map(p => p[0]).join('').slice(0, 2) || 'M').toUpperCase();

  return (
    <div
      className={cn('min-h-screen bg-background text-foreground', resolvedTheme === 'dark' && 'dark')}
      style={paAccentVars(resolvedTheme)}
    >
      {showOnboarding && <Onboarding onComplete={() => { setShowOnboarding(false); localStorage.setItem('pa-onboarded', '1'); }} />}

      <div className="mx-auto flex min-h-screen w-full max-w-[560px] flex-col">
        {/* Header - v1 style */}
        <header className="flex items-center justify-between px-5 pt-6 pb-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Welcome back
            </p>
            <h1 className="text-lg font-semibold text-foreground">{userName}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMenuOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-secondary"
              aria-label="Open menu"
            >
              <Menu className="h-4 w-4" />
            </button>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--v4-accent)] text-sm font-medium text-white">
              {initials.slice(0, 2)}
            </div>
          </div>
        </header>

        <main className="flex-1 px-5 pb-40">
          <Outlet context={{ language: 'en', onSubmitQuery: handleQuerySubmit }} />
        </main>
      </div>

      <InstallPrompt />

      {/* Slide-out menu (light, v1 style) */}
      {menuOpen && (
        <div className="fixed inset-0 z-[60]">
          <div onClick={() => setMenuOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200" />
          <aside className="absolute right-0 top-0 bottom-0 w-72 max-w-[85%] flex flex-col bg-card border-l border-border shadow-2xl animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[var(--v4-accent)]" />
                <span className="text-sm font-semibold text-foreground">Settings</span>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-secondary"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* User card */}
            <div className="px-5 py-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--v4-accent)] text-base font-medium text-white">
                  {initials.slice(0, 2)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{userName}</p>
                  <p className="truncate text-xs text-muted-foreground">{manager?.email}</p>
                </div>
              </div>
            </div>

            {/* Appearance */}
            <div className="px-5 py-4 border-b border-border">
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Appearance</p>
              <ThemeToggle />
            </div>

            {/* Menu items */}
            <nav className="flex-1 overflow-y-auto py-2">
              <p className="px-5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Manage</p>
              <MenuItem to="/pa/deadlines" icon={CalendarClock} label="Deadlines & compliance" onClick={() => setMenuOpen(false)} />
              <MenuItem to="/pa/team" icon={Users} label="Team workload" onClick={() => setMenuOpen(false)} />
              <MenuItem to="/pa/firm" icon={BarChart3} label="Firm KPIs" onClick={() => setMenuOpen(false)} />
              <MenuItem to="/pa/cashflow" icon={Wallet} label="Cash flow" onClick={() => setMenuOpen(false)} />
              <MenuItem to="/pa/alerts" icon={AlertTriangle} label="Alerts" onClick={() => setMenuOpen(false)} />
              <p className="px-5 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">More</p>
              <MenuItem to="/pa/voice" icon={History} label="Chat history" onClick={() => setMenuOpen(false)} />
              <MenuItem to="/pa/digest" icon={Bell} label="Weekly digest" onClick={() => setMenuOpen(false)} />
              <MenuItem to="/pa/email?view=connect" icon={Mail} label="Email accounts" onClick={() => setMenuOpen(false)} />
              <MenuItem to="/pa/pulse" icon={SettingsIcon} label="Pulse" onClick={() => setMenuOpen(false)} />
              <button
                onClick={() => {
                  setMenuOpen(false);
                  const dismissed = localStorage.getItem('pwa-install-dismissed');
                  if (dismissed) localStorage.removeItem('pwa-install-dismissed');
                  window.location.reload();
                }}
                className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-muted/50"
              >
                <Download className="h-4 w-4 text-muted-foreground" />
                <span className="flex-1 text-sm text-foreground">Install app</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            </nav>

            {/* Logout */}
            <div className="border-t border-border p-4">
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-rose-600 transition-colors hover:bg-rose-500/10 dark:text-rose-400"
              >
                <LogOut className="h-4 w-4" />
                <span className="text-sm font-medium">Log out</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Fixed Bottom: Input Bar + Nav */}
      <div className="fixed bottom-0 left-1/2 z-50 w-full max-w-[560px] -translate-x-1/2">
        {/* Chat input bar (PA-specific) */}
        <div className="border-t border-border bg-card/95 px-3 pb-1 pt-2 backdrop-blur-md">
          <div className="mx-auto flex items-end gap-2">
            <div className="flex-1 rounded-2xl border border-border bg-muted/40 px-4 py-2">
              <textarea
                placeholder={isListening ? 'Listening…' : 'Ask about your clients…'}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && inputText.trim()) {
                    e.preventDefault();
                    handleQuerySubmit(inputText.trim());
                    setInputText('');
                  }
                }}
                className="w-full resize-none bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                rows={2}
                disabled={isListening}
              />
            </div>
            <div className="flex flex-col gap-1 pb-1">
              {inputText.trim() ? (
                <button
                  onClick={() => { haptic('light'); handleQuerySubmit(inputText.trim()); setInputText(''); }}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--v4-accent)] transition hover:bg-[var(--v4-accent-hover)] active:scale-95"
                >
                  <Send className="h-4 w-4 text-white" />
                </button>
              ) : (
                <button
                  onClick={openMicModal}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--v4-accent)] transition hover:bg-[var(--v4-accent-hover)] active:scale-95"
                >
                  <Mic className="h-4 w-4 text-white" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Bottom nav - v1 style */}
        <nav className="border-t border-border bg-card/95 px-2 py-2 backdrop-blur-md">
          <div className="flex items-center justify-around">
            {NAV_ITEMS.map(({ href, icon: Icon, label, end }) => (
              <NavLink
                key={href}
                to={href}
                end={end}
                className={({ isActive }) =>
                  cn(
                    'flex flex-1 flex-col items-center gap-1 rounded-xl px-3 py-2 transition-colors',
                    isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className={cn('h-5 w-5', isActive && 'stroke-[2.5] text-[var(--v4-accent)]')} />
                    <span className={cn('text-[10px] font-medium uppercase tracking-wider', isActive && 'font-semibold text-foreground')}>
                      {label}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>

      <SuccessCheck show={showSuccess} />

      {/* Full-screen mic modal - kept dark for focus */}
      {showMicModal && (
        <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-slate-950/98 backdrop-blur-xl animate-in fade-in duration-200">
          <div className="flex flex-1 flex-col items-center justify-center gap-6 px-8">
            <VoiceWaveform isActive={isListening} />
            <p className={cn(
              'max-w-sm text-center text-lg leading-relaxed transition-all duration-300',
              transcript ? 'text-white' : 'text-slate-500'
            )}>
              {transcript || (isListening ? 'Listening…' : 'Hold the mic button and speak…')}
            </p>
          </div>

          <div className="flex flex-col items-center pb-24">
            <button
              onMouseDown={() => { if (!isListening) { startListening(); haptic('medium'); } }}
              onMouseUp={() => { stopListeningAndSubmit(); if (transcript.trim()) { setShowSuccess(true); setTimeout(() => setShowSuccess(false), 1500); } }}
              onTouchStart={(e) => { e.preventDefault(); if (!isListening) { startListening(); haptic('medium'); } }}
              onTouchEnd={(e) => { e.preventDefault(); stopListeningAndSubmit(); if (transcript.trim()) { setShowSuccess(true); setTimeout(() => setShowSuccess(false), 1500); } }}
              className={cn(
                'flex h-24 w-24 select-none items-center justify-center rounded-full transition-all duration-200',
                isListening
                  ? 'bg-red-500 shadow-[0_0_60px_rgba(239,68,68,0.5)] scale-110'
                  : 'bg-[var(--v4-accent)] shadow-[0_0_40px_rgba(53,173,136,0.4)] hover:scale-105 active:scale-95'
              )}
            >
              {isListening ? <MicOff className="h-10 w-10 text-white" /> : <Mic className="h-10 w-10 text-white" />}
            </button>
            <p className="mt-4 text-center text-xs text-slate-500">
              {isListening ? 'Release to send' : 'Hold to speak'}
            </p>
          </div>

          <button
            onClick={() => {
              if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; }
              setIsListening(false);
              setShowMicModal(false);
              setTranscript('');
            }}
            className="absolute top-4 right-4 p-3 text-slate-500 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function ManagerLayout() {
  return (
    <ManagerThemeProvider>
      <ManagerShell />
    </ManagerThemeProvider>
  );
}

function MenuItem({
  to, icon: Icon, label, onClick,
}: { to: string; icon: any; label: string; onClick?: () => void }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-muted/50"
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="flex-1 text-sm text-foreground">{label}</span>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </Link>
  );
}
